import React, { useEffect, useMemo, useRef, useState } from "react"
import { Video as ExpoVideo, VideoProps as ExpoVideoProps, ResizeMode } from "~/ui/expo-av"
import FastImage from "~/ui/FastImage"
import { useTheme } from '~/ui/styled'
import { Mask } from "~widgets/mask"
import { Loading } from "~widgets/loading"
import Icon from "~widgets/icon"
import styled from '~/ui/styled'

const poster = require("../../assets/images/placeholder.jpg")

const VideoWrap = styled.View`
  width: 100%;
  height: 100%;
  max-width: 100%;
  max-height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
`

const VideoComponentInner = styled(ExpoVideo)``

const VideoComponent = (props, ref) => {
  return <VideoComponentInner ref={ref} {...props} />
}

const ForwardedVideoComponent = React.forwardRef(VideoComponent)

const PreviewImage = styled(FastImage)`
  width: 100%;
  height: 100%;
  max-width: 100%;
  max-height: 100%;
  object-fit: contain;
`

interface VideoProps extends ExpoVideoProps {
  forceStart?: boolean
  forceStop?: boolean
  previewImageUri?: string
}

const posterStyle = {
  width: "100%",
  height: "100%",
  maxWidth: "100%",
  maxHeight: "100%",
  objectFit: "contain" as const,
}

const Video: React.FC<VideoProps> = ({
  forceStart = false,
  forceStop,
  previewImageUri,
  onLoad,
  ...props
}) => {
  const theme = useTheme()
  const ref = useRef<{
    playAsync: () => void
    stopAsync: () => void
    setPositionAsync: (millis: number) => void
  }>()
  const [playerVisible, setPlayerVisible] = useState(forceStart)
  const [canPlay, setCanPlay] = useState(forceStart)
  const [usePoster, setUsePoster] = useState(true)
  const [status, setStatus] = useState(null)
  const realPoster = useMemo(() => {
    return previewImageUri ? { uri: previewImageUri } : poster
  }, [previewImageUri])
  useEffect(() => {
    if (playerVisible && canPlay && ref.current) {
      ref.current.playAsync()
    }
  }, [playerVisible, canPlay])
  useEffect(() => {
    if (forceStop && ref.current) {
      ref.current.stopAsync()
    }
  }, [forceStop])
  useEffect(() => {
    if (status && status.durationMillis === status.positionMillis) {
      ref.current.setPositionAsync(0)
    }
  }, [status])
  return (
    <VideoWrap>
      {!playerVisible && (
        <PreviewImage src={realPoster} resizeMode={FastImage.resizeMode.contain} />
      )}
      {playerVisible && (
        <ForwardedVideoComponent
          ref={ref}
          shouldPlay={true}
          src={realPoster}
          posterStyle={posterStyle}
          usePoster={usePoster}
          useNativeControls={true}
          isLooping={false}
          onLoad={(size) => {
            setUsePoster(false)
            setCanPlay(true)
            onLoad?.(size)
          }}
          onPlaybackStatusUpdate={(s) => {
            setStatus(s)
          }}
          {...props}
          resizeMode={ResizeMode.CONTAIN}
        />
      )}
      {playerVisible && !canPlay && (
        <Mask backgroundColor="transparent">
          <Loading color={theme.colors.border} size={"large"}></Loading>
        </Mask>
      )}
      {!playerVisible && !canPlay && (
        <Mask backgroundColor="transparent" onClick={() => setPlayerVisible(true)}>
          <Icon name="24gf-playCircle" color={theme.colors.blockBackground} size={60}></Icon>
        </Mask>
      )}
    </VideoWrap>
  )
}

export default Video
